import { useEffect, useMemo } from 'react'
import { findCancellationUsers, findCancellationUsersVariables } from '__generatedTypes__/findCancellationUsers'
import { SearchTypeEnum } from '__generatedTypes__/globalTypes'
import { FIRST_PAGE, Params, useQueryWithParams } from 'hooks/useQueryWithParams'
import { FIND_CANCELLATION_USERS } from 'queries/membershipCancellation'
import { DEFAULT_PAGE_SIZE } from '../constants'
import { CancellationUsersQueryFilters } from '../types'

const castCancellationUsersParams = ({
  page,
  take,
  filters,
}: Params<CancellationUsersQueryFilters>): findCancellationUsersVariables => ({
  input: {
    platformId: filters.platformId ?? '',
    startDate: filters.startDate,
    endDate: filters.endDate,
    cancellationReason: filters.cancellationReason,
    hasCancellationComment: filters.hasCancellationComment,
    search: filters.search ? { query: filters.search, type: SearchTypeEnum.ILike } : undefined,
    skip: (page - 1) * take,
    take,
  },
  includeCancellationComment: !!filters.hasCancellationComment,
})

export const useCancellationUsersQuery = (
  filters: CancellationUsersQueryFilters,
  { skip = false }: { skip?: boolean } = {}
) => {
  const queryFilters = useMemo(
    () => ({
      platformId: filters.platformId,
      startDate: filters.startDate,
      endDate: filters.endDate,
      cancellationReason: filters.cancellationReason,
      hasCancellationComment: filters.hasCancellationComment,
      search: filters.search,
    }),
    [
      filters.cancellationReason,
      filters.endDate,
      filters.hasCancellationComment,
      filters.platformId,
      filters.search,
      filters.startDate,
    ]
  )

  const usersQuery = useQueryWithParams<
    findCancellationUsers,
    findCancellationUsersVariables,
    'findCancellationUsers',
    'items',
    'count',
    CancellationUsersQueryFilters
  >(
    FIND_CANCELLATION_USERS,
    'findCancellationUsers',
    'items',
    'count',
    castCancellationUsersParams,
    undefined,
    {
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    },
    {
      take: DEFAULT_PAGE_SIZE,
    }
  )

  useEffect(() => {
    if (skip) {
      return
    }

    void usersQuery.refetch({ page: FIRST_PAGE, filters: queryFilters })
  }, [queryFilters, skip, usersQuery.refetch])

  return usersQuery
}
